import { useState, useCallback, useEffect, useMemo } from 'react'
import { Alert, Box, Button, Chip, MenuItem, Select, Snackbar, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridRowSelectionModel,
  GridActionsCellItem,
  GridRowModes,
  GridRowModesModel,
  GridEventListener,
  GridRowEditStopReasons,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material'
import { usePOTracking } from '@/hooks/usePOTracking'
import { useAuth } from '@/hooks/useAuth'
import { EvidenceDialog } from '@/components/InvoiceAnalyser/EvidenceDialog'
import type { DatabaseInvoice, ColumnDefinition } from '@/types/database'
import type { InvoiceExtraction, Evidence } from '@/types/invoiceAnalyser'
import type { PurchaseOrder } from '@/types/po'
import {
  STATUS_OPTIONS,
  STATUS_LABELS,
  STATUS_COLORS,
  normalizeStatus,
  isOverdue,
  isDueSoon,
  todayIso,
  type InvoiceStatus,
} from '@/utils/invoiceStatus'
import { ENTITY_OPTIONS } from '@/utils/invoiceEntity'
import { LEGAL_COUNSEL_OPTIONS } from '@/utils/legalCounsel'
import {
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_COLORS,
  normalizeDocumentType,
  signMismatchWarning,
} from '@/utils/documentType'

/** Column view presets to keep the table focused on invoice control by default. */
type ViewPreset = 'standard' | 'tax' | 'all'

const STANDARD_VISIBLE = new Set<string>([
  'actions', 'status', 'documentType', 'supplierName', 'recipient', 'entity', 'projectName',
  'rechnungsnummer', 'originalInvoiceId', 'dateInvoice', 'dueDate', 'servicePeriodStart', 'servicePeriodEnd',
  'netto', 'brutto', 'auslagen', 'currency', 'poId', 'approver', 'legalCounsel', 'comment',
])

const TAX_VISIBLE = new Set<string>([
  ...STANDARD_VISIBLE,
  'vatAmount', 'taxFreeAmount', 'taxableNetAmount', 'ustId', 'original',
])

const ALL_COLUMN_FIELDS = [
  'actions', 'status', 'documentType', 'supplierName', 'recipient', 'entity', 'projectName',
  'rechnungsnummer', 'originalInvoiceId', 'dateInvoice', 'dueDate', 'servicePeriodStart', 'servicePeriodEnd',
  'netto', 'brutto', 'auslagen', 'currency',
  'poId', 'approver', 'approvalDate', 'legalCounsel', 'vatAmount', 'taxFreeAmount',
  'taxableNetAmount', 'ustId', 'confidence', 'comment', 'source',
  'sourceDocumentId', 'original', 'createdAt',
]

function buildVisibilityModel(preset: ViewPreset): GridColumnVisibilityModel {
  if (preset === 'all') return {}
  const visible = preset === 'tax' ? TAX_VISIBLE : STANDARD_VISIBLE
  const model: GridColumnVisibilityModel = {}
  ALL_COLUMN_FIELDS.forEach((field) => {
    if (!visible.has(field)) model[field] = false
  })
  return model
}

const EMPTY_EVIDENCE: Evidence = {
  supplier: null, invoice_date: null, invoice_number: null, currency: null,
  total_net: null, total_gross: null, vat_amount: null, tax_free_amount: null,
  taxable_net_amount: null, project_name: null, recipient: null,
}

/** True when a saved invoice carries analyser audit data worth showing. */
const hasAuditTrail = (inv: DatabaseInvoice): boolean =>
  !!inv.evidence || (inv.warnings?.length ?? 0) > 0

/** Adapt a stored DatabaseInvoice back into the InvoiceExtraction shape EvidenceDialog expects. */
function toExtraction(inv: DatabaseInvoice): InvoiceExtraction {
  return {
    supplier: inv.supplierName,
    invoice_date: inv.dateInvoice,
    invoice_number: inv.rechnungsnummer,
    currency: inv.currency,
    total_net: inv.netto,
    total_gross: inv.brutto,
    vat_amount: inv.vatAmount,
    tax_free_amount: inv.taxFreeAmount,
    taxable_net_amount: inv.taxableNetAmount,
    vat_breakdown: inv.vatBreakdown,
    tax_free_breakdown: inv.taxFreeBreakdown,
    project_name: inv.projectName,
    recipient: inv.recipient,
    ust_id: inv.ustId,
    po_id: inv.poId,
    evidence: inv.evidence ?? EMPTY_EVIDENCE,
    warnings: inv.warnings ?? [],
    confidence: inv.confidence ?? 'low',
    document_type: inv.documentType ?? 'invoice',
  }
}

const MAX_PO_DESCRIPTION_LENGTH = 10

const truncateDescription = (description: string | null): string => {
  if (!description) return ''
  const trimmed = description.trim()
  if (!trimmed) return ''
  return trimmed.length > MAX_PO_DESCRIPTION_LENGTH
    ? `${trimmed.slice(0, MAX_PO_DESCRIPTION_LENGTH)}...`
    : trimmed
}

const formatPOLabel = (po: PurchaseOrder): string => {
  const truncated = truncateDescription(po.description)
  return truncated ? `${po.poNumber} - ${truncated}` : po.poNumber
}

/** Short reference label for linking a Storno/Gutschrift to its original invoice. */
const formatInvoiceRef = (inv: DatabaseInvoice): string => {
  const num = inv.rechnungsnummer || '(no number)'
  const date = inv.dateInvoice ? ` · ${inv.dateInvoice}` : ''
  return `${num}${date}`
}

const getInvoiceYear = (dateStr: string | null): number | null => {
  if (!dateStr) return null
  const trimmed = dateStr.trim()
  if (!trimmed) return null

  const isoMatch = /^(\d{4})-\d{2}-\d{2}/.exec(trimmed)
  if (isoMatch) return Number(isoMatch[1])

  const deMatch = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed)
  if (deMatch) return Number(deMatch[3])

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.getFullYear()
}

/**
 * Display any stored invoice date consistently as DD.MM.YYYY, regardless of how it was
 * stored (analyser saves ISO YYYY-MM-DD; dashboard/Excel import use DD.MM.YYYY).
 */
const formatDateCell = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const s = String(value).trim()
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`
  return s
}

interface DatabaseTableProps {
  invoices: DatabaseInvoice[]
  columns: ColumnDefinition[]
  highlightId?: string | null
  onEdit: (id: string, data: Partial<DatabaseInvoice>) => Promise<void> | void
  onDelete: (id: string) => Promise<void> | void
  onCreate: (data: Partial<DatabaseInvoice>) => Promise<void> | void
  onBulkStatus: (
    ids: string[],
    status: InvoiceStatus,
    approver?: string | null,
    approvalDate?: string | null,
  ) => Promise<void> | void
}

export function DatabaseTable({
  invoices,
  highlightId,
  onEdit,
  onDelete,
  onCreate,
  onBulkStatus,
}: DatabaseTableProps) {
  const apiBaseUrl = (import.meta.env.VITE_API_URL || '/v1').replace(/\/$/, '')
  const { user } = useAuth()
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [rows, setRows] = useState<DatabaseInvoice[]>(invoices)
  const [viewPreset, setViewPreset] = useState<ViewPreset>('standard')
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => buildVisibilityModel('standard')
  )
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])
  const [bulkStatus, setBulkStatus] = useState<InvoiceStatus>('approved')
  const [auditInvoice, setAuditInvoice] = useState<DatabaseInvoice | null>(null)
  const [signWarning, setSignWarning] = useState<string | null>(null)

  // PO Tracking - load all POs for per-row year filtering
  const { usePOs } = usePOTracking()
  const { data: pos = [] } = usePOs()

  const handleViewPresetChange = useCallback((_: unknown, next: ViewPreset | null) => {
    if (!next) return
    setViewPreset(next)
    setColumnVisibilityModel(buildVisibilityModel(next))
  }, [])

  // Bulk status change for all selected rows (auto-stamps approver when approving)
  const handleApplyBulk = useCallback(async () => {
    const ids = selectionModel.map(String)
    if (ids.length === 0) return
    let approver: string | null | undefined
    let approvalDate: string | null | undefined
    if (bulkStatus === 'approved') {
      approver = user?.fullName || user?.username || null
      approvalDate = todayIso()
    }
    await onBulkStatus(ids, bulkStatus, approver, approvalDate)
    setSelectionModel([])
  }, [selectionModel, bulkStatus, onBulkStatus, user])

  // Update rows when invoices change
  useEffect(() => {
    setRows(invoices)
  }, [invoices])

  // Handle row edit stop
  const handleRowEditStop: GridEventListener<'rowEditStop'> = useCallback((params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }, [])

  // Handle edit click
  const handleEditClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }))
  }, [])

  // Handle save click
  const handleSaveClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }))
  }, [])

  // Handle delete click
  const handleDeleteClick = useCallback((id: string) => () => {
    void onDelete(id)
  }, [onDelete])

  // Handle cancel click
  const handleCancelClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }))
  }, [])

  // Process row update
  const processRowUpdate = useCallback(async (newRow: DatabaseInvoice, oldRow: DatabaseInvoice) => {
    const updatedRow = { ...newRow }

    // Auto-stamp approver + approval date when an invoice is marked approved.
    if (normalizeStatus(updatedRow.status) === 'approved' && !updatedRow.approver) {
      updatedRow.approver = user?.fullName || user?.username || null
      if (!updatedRow.approvalDate) updatedRow.approvalDate = todayIso()
    }

    // Non-blocking plausibility nudge: credit notes / cancellations are usually
    // negative. Sign is never changed automatically — we only warn.
    const mismatch = signMismatchWarning(updatedRow.documentType, updatedRow.brutto)
    if (mismatch) setSignWarning(mismatch)

    const isNewRow = oldRow.id.startsWith('new-')
    setRows((prev) => prev.map((row) => (row.id === oldRow.id ? updatedRow : row)))

    try {
      if (isNewRow) {
        await onCreate(updatedRow)
      } else {
        await onEdit(updatedRow.id, updatedRow)
      }
      return updatedRow
    } catch (error) {
      setRows((prev) => {
        if (isNewRow) {
          return prev.filter((row) => row.id !== updatedRow.id)
        }
        return prev.map((row) => (row.id === updatedRow.id ? oldRow : row))
      })
      throw error instanceof Error ? error : new Error('Failed to save row')
    }
  }, [onCreate, onEdit, user])

  // Handle row modes model change
  const handleRowModesModelChange = useCallback((newModel: GridRowModesModel) => {
    setRowModesModel(newModel)
  }, [])

  // Handle add new row
  const handleAddNew = useCallback(() => {
    const newId = `new-${Date.now()}`
    const newRow: DatabaseInvoice = {
      id: newId,
      supplierName: '',
      dateInvoice: null,
      brutto: null,
      netto: null,
      vatAmount: null,
      taxFreeAmount: null,
      taxableNetAmount: null,
      rechnungsnummer: '',
      comment: '',
      currency: null,
      projectName: null,
      recipient: null,
      entity: null,
      ustId: null,
      confidence: null,
      source: 'dashboard',
      customFields: {},
      vatBreakdown: null,
      taxFreeBreakdown: null,
      sourceDocumentId: null,
      poId: null,
      originalCurrency: null,
      originalBrutto: null,
      originalNetto: null,
      originalVatAmount: null,
      originalTaxFreeAmount: null,
      originalTaxableNetAmount: null,
      fxRate: null,
      fxRateDate: null,
      fxRateSource: null,
      status: 'open',
      approver: null,
      approvalDate: null,
      dueDate: null,
      auslagen: null,
      originalAuslagen: null,
      servicePeriodStart: null,
      servicePeriodEnd: null,
      legalCounsel: null,
      documentType: 'invoice',
      originalInvoiceId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setRows((prev) => [newRow, ...prev])
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'supplierName' } }))
  }, [])

  // Base columns
  const baseColumns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 130,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{ color: 'primary.main' }}
              onClick={handleSaveClick(id.toString())}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id.toString())}
              color="inherit"
            />,
          ]
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id.toString())}
            color="inherit"
          />,
          ...(hasAuditTrail(row as DatabaseInvoice)
            ? [
                <GridActionsCellItem
                  icon={<FactCheckIcon />}
                  label="Audit trail"
                  onClick={() => setAuditInvoice(row as DatabaseInvoice)}
                  color="inherit"
                />,
              ]
            : []),
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id.toString())}
            color="inherit"
          />,
        ]
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: STATUS_OPTIONS,
      renderCell: (params) => {
        const status = normalizeStatus(params.value)
        const row = params.row as DatabaseInvoice
        const overdue = isOverdue(row.dueDate, status, row.documentType)
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            <Chip size="small" label={STATUS_LABELS[status]} color={STATUS_COLORS[status]} />
            {overdue && (
              <Chip size="small" label="Overdue" color="error" variant="outlined" />
            )}
          </Stack>
        )
      },
    },
    {
      field: 'documentType',
      headerName: 'Type',
      width: 140,
      editable: true,
      type: 'singleSelect',
      valueOptions: DOCUMENT_TYPE_OPTIONS,
      renderCell: (params) => {
        const dt = normalizeDocumentType(params.value)
        const row = params.row as DatabaseInvoice
        const warn = signMismatchWarning(dt, row.brutto)
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            <Chip size="small" label={DOCUMENT_TYPE_LABELS[dt]} color={DOCUMENT_TYPE_COLORS[dt]} />
            {warn && (
              <Box component="span" title={warn} sx={{ color: 'warning.main', cursor: 'help', fontWeight: 700 }}>
                ⚠
              </Box>
            )}
          </Stack>
        )
      },
    },
    { field: 'supplierName', headerName: 'Supplier', width: 180, editable: true },
    {
      field: 'dateInvoice',
      headerName: 'Date',
      width: 120,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'dueDate',
      headerName: 'Due',
      width: 120,
      editable: true,
      type: 'string',
      renderCell: (params) => {
        if (!params.value) return '–'
        const row = params.row as DatabaseInvoice
        const overdue = isOverdue(row.dueDate, row.status, row.documentType)
        const soon = !overdue && isDueSoon(row.dueDate, row.status, row.documentType)
        const color = overdue ? 'error.main' : soon ? 'warning.main' : 'inherit'
        return (
          <Box
            component="span"
            sx={{ color, fontWeight: overdue || soon ? 600 : 400 }}
          >
            {formatDateCell(params.value)}
          </Box>
        )
      },
    },
    {
      field: 'servicePeriodStart',
      headerName: 'Service From',
      width: 120,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'servicePeriodEnd',
      headerName: 'Service To',
      width: 120,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'brutto',
      headerName: 'Gross',
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'netto',
      headerName: 'Net',
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'vatAmount',
      headerName: 'VAT',
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'taxFreeAmount',
      headerName: 'Tax-Free',
      width: 110,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'taxableNetAmount',
      headerName: 'Taxable Net',
      width: 120,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'auslagen',
      headerName: 'Auslagen',
      width: 110,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    { field: 'rechnungsnummer', headerName: 'Invoice #', width: 140, editable: true },
    {
      field: 'originalInvoiceId',
      headerName: 'Original Invoice',
      width: 220,
      editable: true,
      type: 'singleSelect',
      valueOptions: (params: any) => {
        const row = params.row as DatabaseInvoice
        const options: { value: string | null; label: string }[] = [{ value: null, label: '—' }]
        const supplierKey = (row.supplierName || '').trim().toLowerCase()
        rows.forEach((inv) => {
          if (inv.id === row.id || inv.id.startsWith('new-')) return
          if (inv.documentType && inv.documentType !== 'invoice') return // link to an actual invoice
          if (supplierKey && (inv.supplierName || '').trim().toLowerCase() !== supplierKey) return
          options.push({ value: inv.id, label: formatInvoiceRef(inv) })
        })
        // Keep the currently linked original selectable even if the supplier filter would hide it.
        if (row.originalInvoiceId && !options.some((o) => o.value === row.originalInvoiceId)) {
          const linked = rows.find((i) => i.id === row.originalInvoiceId)
          if (linked) options.push({ value: linked.id, label: formatInvoiceRef(linked) })
        }
        return options
      },
      valueFormatter: (value: string | null) => {
        if (!value) return '—'
        const inv = rows.find((i) => i.id === value)
        return inv ? formatInvoiceRef(inv) : '—'
      },
    },
    { field: 'comment', headerName: 'Comment', width: 200, editable: true },
    { field: 'currency', headerName: 'Currency', width: 80, editable: true },
    { field: 'projectName', headerName: 'Project', width: 150, editable: true },
    { field: 'recipient', headerName: 'Recipient', width: 180, editable: true },
    {
      field: 'entity',
      headerName: 'Entity',
      width: 200,
      editable: true,
      type: 'singleSelect',
      valueOptions: ENTITY_OPTIONS,
      valueFormatter: (value: string | null) => value || 'Unassigned',
    },
    { field: 'ustId', headerName: 'VAT ID', width: 120, editable: true },
    { field: 'confidence', headerName: 'Confidence', width: 100, editable: true },
    { field: 'source', headerName: 'Origin', width: 100, editable: false },
    {
      field: 'sourceDocumentId',
      headerName: 'Source',
      width: 140,
      editable: false,
      renderCell: (params) => {
        if (!params.value) return '-'
        const href = `${apiBaseUrl}/documents/${params.value}/file`
        return (
          <a href={href} target="_blank" rel="noreferrer">
            Download
          </a>
        )
      },
    },
    {
      field: 'poId',
      headerName: 'PO Number',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: useMemo(() => {
        return (params) => {
          const row = params.row as DatabaseInvoice
          const options = [{ value: null as string | null, label: 'Unassigned' }]
          if (pos.length === 0) return options

          const invoiceYear = getInvoiceYear(row.dateInvoice)
          const filtered = invoiceYear ? pos.filter((po) => po.year === invoiceYear) : pos

          filtered.forEach((po: PurchaseOrder) => {
            options.push({ value: po.id, label: formatPOLabel(po) })
          })

          if (row.poId) {
            const selected = pos.find((po) => po.id === row.poId)
            const alreadyIncluded = selected && filtered.some((po) => po.id === selected.id)
            if (selected && !alreadyIncluded) {
              options.push({ value: selected.id, label: formatPOLabel(selected) })
            }
          }

          return options
        }
      }, [pos]),
      valueFormatter: (value: string | null) => {
        const po = pos.find((p: PurchaseOrder) => p.id === value)
        return po ? formatPOLabel(po) : 'Unassigned'
      },
    },
    { field: 'approver', headerName: 'Approver', width: 160, editable: true },
    {
      field: 'legalCounsel',
      headerName: 'Legal Counsel',
      width: 180,
      editable: true,
      type: 'singleSelect',
      valueOptions: LEGAL_COUNSEL_OPTIONS,
      valueFormatter: (value: string | null) => value || 'Unassigned',
    },
    {
      field: 'approvalDate',
      headerName: 'Approved On',
      width: 130,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'original',
      headerName: 'Original',
      width: 180,
      editable: false,
      renderCell: (params) => {
        const row = params.row as DatabaseInvoice
        if (!row.originalCurrency || row.originalNetto == null) return '-'
        const value = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(row.originalNetto)
        return `${value} ${row.originalCurrency} (FX ${row.fxRateDate || '-'})`
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      editable: false,
      valueFormatter: (value) => {
        if (!value) return ''
        return new Date(value).toLocaleString('de-DE')
      },
    },
  ]

  const Toolbar = useCallback(() => (
    <GridToolbarContainer>
      <GridToolbarQuickFilter />
      <GridToolbarFilterButton />
      <GridToolbarColumnsButton />
      <Box sx={{ flexGrow: 1 }} />
      {selectionModel.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {selectionModel.length} selected
          </Typography>
          <Select
            size="small"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value as InvoiceStatus)}
            sx={{ minWidth: 130 }}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{`Set: ${o.label}`}</MenuItem>
            ))}
          </Select>
          <Button variant="contained" size="small" onClick={() => void handleApplyBulk()}>
            Apply
          </Button>
        </Stack>
      )}
      <ToggleButtonGroup
        value={viewPreset}
        exclusive
        onChange={handleViewPresetChange}
        size="small"
        sx={{ mr: 1 }}
      >
        <ToggleButton value="standard">Standard</ToggleButton>
        <ToggleButton value="tax">Tax details</ToggleButton>
        <ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>
      <Button startIcon={<AddIcon />} onClick={handleAddNew} size="small">
        Add Invoice
      </Button>
    </GridToolbarContainer>
  ), [handleAddNew, viewPreset, handleViewPresetChange, selectionModel, bulkStatus, handleApplyBulk])

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <EvidenceDialog
        open={!!auditInvoice}
        onClose={() => setAuditInvoice(null)}
        extraction={auditInvoice ? toExtraction(auditInvoice) : null}
      />
      <Snackbar
        open={!!signWarning}
        autoHideDuration={6000}
        onClose={() => setSignWarning(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setSignWarning(null)}>
          {signWarning}
        </Alert>
      </Snackbar>
      <DataGrid
        rows={rows}
        columns={baseColumns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        checkboxSelection
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={setSelectionModel}
        keepNonExistentRowsSelected
        processRowUpdate={processRowUpdate}
        getRowClassName={(params) => (highlightId && params.id === highlightId ? 'invoice-highlight' : '')}
        pageSizeOptions={[25, 50, 100]}
        pagination
        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
        disableRowSelectionOnClick
        slots={{
          toolbar: Toolbar,
        }}
        sx={{
          '& .invoice-highlight': {
            backgroundColor: 'rgba(255, 235, 133, 0.4)',
            '&:hover': {
              backgroundColor: 'rgba(255, 235, 133, 0.6)',
            },
          },
        }}
      />
    </Box>
  )
}
